/*
 * formModal — Alpine component for .mh-form-modal.
 *
 * Single global modal mounted once in layout.antlers.html. Listens for
 * clicks on any [data-form-trigger="{handle}"] button anywhere on the
 * page, opens the modal, and reveals the <section> matching the
 * requested form handle.
 *
 * Selection state ("which form is currently shown") lives on
 * `activeHandle`. Each form section uses x-show on `activeHandle ===
 * '{handle}'` so only one is visible at a time.
 *
 * Focus management:
 *   - Trigger click: remember the element that fired the trigger so we
 *     can return focus to it on close.
 *   - Open: @alpinejs/focus's x-trap.inert.noscroll handles trap +
 *     scroll lock + inert outside the panel.
 *   - Close: restore focus to the original trigger.
 *
 * Keyboard model:
 *   - Escape closes (@keydown.escape.window in the partial — x-trap sets
 *     focus-trap's escapeDeactivates to false, so it will not do this for us)
 *   - Backdrop click closes (in the partial)
 *   - Close button closes
 *
 * Scroll position across the lock (MOHO-133): x-trap's `noscroll` locks the
 * page by setting document.documentElement.style.overflow = 'hidden' and
 * nothing else — it never records where the page was. Desktop engines keep the
 * offset and simply stop scrolling, which is why none of this reproduces in
 * dev tools or in headless WebKit. Mobile Safari does not: with the lock on,
 * the document's scrollable height is zero, so the position is clamped away,
 * and the reader is returned to the top of the page rather than to the CTA
 * they came from.
 *
 * We take the reading before the lock goes on and put it back after — see
 * restoreScroll() for why putting it back is not one line.
 *
 * Visible-viewport tracking (MOHO-133): while open we mirror
 * visualViewport.height/offsetTop onto --mh-vv-height/--mh-vv-top, which
 * .mh-form-modal__viewport sizes itself from. CSS alone can't do this — dvh
 * follows the browser chrome but not the on-screen keyboard, so on a phone
 * the foot of a long form (Send Enquiry, and the Difference panel stacked
 * under it) ended up below the fold with the panel already scrolled to its
 * end, and the form read as frozen. Listeners are bound only while open, and
 * the whole thing is a progressive enhancement: without visualViewport the
 * CSS falls back to 100dvh, then 100vh.
 *
 * The document-level [data-form-trigger] listener is installed in init()
 * and removed in destroy() so re-init across Swup page transitions
 * doesn't double-bind.
 */
export default function formModal() {
    return {
        open: false,
        activeHandle: null,
        _lastTrigger: null,
        _onDocClick: null,
        _onEnquirySuccess: null,
        _onViewportChange: null,
        _scrollY: null,

        init() {
            // Single document-level click listener — works for triggers
            // rendered anywhere on the page (including inside Swup-replaced
            // content, which is still in #swup, in the same document).
            this._onDocClick = (event) => {
                const trigger = event.target.closest('[data-form-trigger]');
                if (!trigger) return;
                event.preventDefault();
                // The header enquiry action remains visible above the mobile
                // drawer. Release its focus/scroll trap before opening ours.
                if (this.$store.nav.mobileOpen) {
                    this.$store.nav.mobileOpen = false;
                    this.$nextTick(() => this.handleTrigger(trigger.dataset.formTrigger, trigger));
                    return;
                }
                this.handleTrigger(trigger.dataset.formTrigger, trigger);
            };
            document.addEventListener('click', this._onDocClick);

            // Close when an enquiry submits successfully — the modal lives
            // outside #swup, so the Swup navigate() to the thank-you page won't
            // tear it down; without this it would stay overlaid on the new page.
            this._onEnquirySuccess = () => this.close();
            window.addEventListener('enquiry:success', this._onEnquirySuccess);
        },

        destroy() {
            if (this._onDocClick) {
                document.removeEventListener('click', this._onDocClick);
                this._onDocClick = null;
            }
            if (this._onEnquirySuccess) {
                window.removeEventListener('enquiry:success', this._onEnquirySuccess);
                this._onEnquirySuccess = null;
            }
            this.untrackViewport();
        },

        /**
         * Mirror the visible viewport onto CSS custom properties so the modal
         * shell can never extend past the fold. No-op where visualViewport is
         * unsupported — the CSS fallback chain covers it.
         */
        trackViewport() {
            const viewport = window.visualViewport;
            if (!viewport || this._onViewportChange) {
                return;
            }
            this._onViewportChange = () => {
                this.$el.style.setProperty('--mh-vv-height', `${viewport.height}px`);
                this.$el.style.setProperty('--mh-vv-top', `${viewport.offsetTop}px`);
            };
            this._onViewportChange();
            viewport.addEventListener('resize', this._onViewportChange);
            viewport.addEventListener('scroll', this._onViewportChange);
        },

        untrackViewport() {
            if (!this._onViewportChange) {
                return;
            }
            window.visualViewport.removeEventListener('resize', this._onViewportChange);
            window.visualViewport.removeEventListener('scroll', this._onViewportChange);
            this._onViewportChange = null;
            // The properties are deliberately left in place. Clearing them here
            // would resize the shell mid leave-transition, and they are inert
            // while the modal is closed — trackViewport() refreshes them on the
            // next open.
        },

        handleTrigger(handle, triggerEl) {
            if (!handle) return;
            this._lastTrigger = triggerEl ?? null;
            this.activeHandle = handle;
            // Read before `open` flips, because the x-trap effect that follows
            // is what destroys the value on iOS. Re-triggering while already
            // open must not overwrite it with the clamped 0.
            if (!this.open) {
                this._scrollY = window.scrollY;
            }
            this.trackViewport();
            this.open = true;
        },

        close() {
            // Escape is a window listener, so this runs on every Escape
            // anywhere on the page. Without the guard a stray keypress would
            // scroll the reader to a position saved by some earlier open.
            if (!this.open) {
                return;
            }

            this.open = false;
            this.activeHandle = null;
            this.untrackViewport();

            const trigger = this._lastTrigger;
            const scrollY = this._scrollY;
            this._lastTrigger = null;
            this._scrollY = null;

            // Next tick, so x-trap's effect has released the scroll lock.
            this.$nextTick(() => {
                // preventScroll because restoreScroll() owns the position from
                // here; letting focus() scroll as well would just fight it.
                if (trigger) {
                    trigger.focus({ preventScroll: true });
                }
                if (scrollY !== null) {
                    this.restoreScroll(scrollY);
                }
            });
        },

        /**
         * Put the page back where the reader left it, and keep checking that it
         * stayed there.
         *
         * One scrollTo is not enough, for two reasons that only bite on mobile
         * Safari and only in combination:
         *
         *   1. Releasing the lock does not hand the document its scroll height
         *      back synchronously. A scrollTo issued in the same tick is
         *      measured against a maxScroll that is still 0, so it clamps to
         *      the top — the restore silently does nothing.
         *   2. focus-trap's own return-focus runs in a setTimeout after us.
         *      Safari never focuses a <button> on tap, so the node it captured
         *      before activation is <body>, which it then focuses with
         *      preventScroll unset. Alpine passes no preventScroll option.
         *
         * So: re-assert each frame until the position holds for two frames
         * running. Bounded at 8 frames (~130ms) so a reader who starts
         * scrolling immediately is never fought for long, and it costs nothing
         * on engines that never lost the position — there the first check
         * already matches and no scrollTo is issued at all.
         *
         * behavior: 'instant' is load-bearing: _reset.css puts
         * scroll-behavior: smooth on <html>, so a plain scrollTo would animate
         * the correction and read as the page sliding around after close.
         *
         * @param {number} y - scrollY captured before the modal opened
         */
        restoreScroll(y) {
            let frames = 0;
            let stable = 0;

            const apply = () => {
                if (Math.abs(window.scrollY - y) > 1) {
                    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
                    stable = 0;
                } else {
                    stable += 1;
                }

                frames += 1;

                if (stable < 2 && frames < 8) {
                    requestAnimationFrame(apply);
                }
            };

            apply();
        },
    };
}
